import React, {useEffect} from 'react';
import SidebarMenuSection from './SidebarMenuSection';
import SidebarMenuLink from './SidebarMenuLink';
import {SidebarMenuSections, MenuSection, MenuItem} from './stores/sidebarMenu';
import {useStoreActions, useStoreState} from '@/store';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';

const AutoSidebarMenu: React.FC = () => {
    const menuStructure = useStoreState(state => state.sidebarMenu.menuStructure);
    const isLoading = useStoreState(state => state.sidebarMenu.isLoading);
    const error = useStoreState(state => state.sidebarMenu.error);
    const dynamicChildrenCache = useStoreState(state => state.sidebarMenu.dynamicChildrenCache);
    const dynamicChildrenLoading = useStoreState(state => state.sidebarMenu.dynamicChildrenLoading);
    const loadMenuStructure = useStoreActions(actions => actions.sidebarMenu.loadMenuStructure);
    const loadDynamicChildren = useStoreActions(actions => actions.sidebarMenu.loadDynamicChildren);

    // Note: loadMenuStructure is called by BaseRouter after config loads
    // No need to call it here to avoid duplicate requests

    // Load dynamic children for sections and items that need them
    useEffect(() => {
        if (menuStructure.length > 0) {
            menuStructure.forEach(section => {
                // Load dynamic children for sections
                if (section.meta.hasDynamicChildren && !dynamicChildrenCache[section.name] && !dynamicChildrenLoading[section.name]) {
                    loadDynamicChildren(section.name);
                }
        
                // Load dynamic children for items
                section.items.forEach(item => {
                    if (item.meta.visible !== false && item.meta.hasDynamicChildren && !dynamicChildrenCache[item.path] && !dynamicChildrenLoading[item.path]) {
                        loadDynamicChildren(item.path);
                    }
                });
            });
        }
    }, [menuStructure, dynamicChildrenCache, dynamicChildrenLoading, loadDynamicChildren]);

    // Normalize icon name - remove 'mdi ' prefix if present since components add it
    const normalizeIcon = (iconName: string) => {
        if (!iconName) {return 'mdi-folder';}
        return iconName.replace(/^mdi\s+/, '');
    };

    const renderMenuItem = (item: MenuItem, sectionName: string) => {
        const activeRoutes: string[] = [];
    
        // If explicit activeRoutes are configured in meta, use those
        if (item.meta.activeRoutes && item.meta.activeRoutes.length > 0) {
            activeRoutes.push(...item.meta.activeRoutes);
        }
    
        // Handle dynamic children items
        if (item.meta.hasDynamicChildren) {
            const dynamicChildren = dynamicChildrenCache[item.path] || [];
            const isLoadingChildren = dynamicChildrenLoading[item.path] || false;
      
            // Add dynamic children routes to active routes
            dynamicChildren.forEach(child => {
                activeRoutes.push(child.route);
            });
      
            // Add main item route (unless explicit activeRoutes were provided)
            if (!item.meta.activeRoutes && item.meta.routes?.list) {
                activeRoutes.push(item.meta.routes.list);
            }
      
            const uniqueActiveRoutes = [...new Set(activeRoutes)].sort((a, b) => b.length - a.length);
      
            return (
                <React.Fragment key={`${sectionName}-${item.path}-dynamic`}>
                    <SidebarMenuLink 
                        route={item.meta.routes?.list || `/${item.path}`}
                        activeRoutes={uniqueActiveRoutes}
                    >
                        {item.meta.icon && <Icon name={item.meta.icon} style={{marginRight: '8px'}} />}
                        <span className="hide-menu">{item.meta.title}</span>
                        {isLoadingChildren && <Icon name="mdi-loading" spin style={{float: 'right', opacity: 0.6}} />}
                        {!isLoadingChildren && dynamicChildren.length > 0 && <Icon name="mdi-chevron-down" style={{float: 'right', opacity: 0.6}} />}
                    </SidebarMenuLink>
          
                    {/* Render dynamic children */}
                    {dynamicChildren.map(child => (
                        <SidebarMenuLink 
                            key={child.id}
                            route={child.route}
                            activeRoutes={[child.route]}
                        >
                            <Icon name="mdi-circle-small" style={{marginRight: '8px', marginLeft: '16px'}} />
                            <span className="hide-menu" style={{fontSize: '0.9em'}}>{child.title}</span>
                        </SidebarMenuLink>
                    ))}
                </React.Fragment>
            );
        }
    
        // Handle tabbed items
        if (item.meta.hasTabNavigation && item.meta.tabs) {
            // Use explicit activeRoutes if provided, otherwise build from tabs
            if (!item.meta.activeRoutes) {
                // Add all tab routes
                item.meta.tabs.forEach(tab => {
                    activeRoutes.push(tab.route);
                });
        
                // Also add base route if it exists (for matching purposes only)
                const baseRoute = item.meta.routes?.base || item.meta.routes?.list;
                if (baseRoute) {
                    activeRoutes.push(baseRoute);
                }
            }
      
            // For tabbed items with a list view, link to the list
            // Only use tab route if there's no list route (detail-only pages)
            const menuRoute = item.meta.routes?.list || item.meta.routes?.base || `/${item.path}`;
      
            const uniqueActiveRoutes = [...new Set(activeRoutes)].sort((a, b) => b.length - a.length);
      
            return (
                <SidebarMenuLink 
                    route={menuRoute}
                    activeRoutes={uniqueActiveRoutes}
                >
                    {item.meta.icon && <Icon name={item.meta.icon} style={{marginRight: '8px'}} />}
                    <span className="hide-menu">{item.meta.title}</span>
                </SidebarMenuLink>
            );
        }
    
        // Handle regular items (existing logic)
        // Use explicit activeRoutes if provided
        if (!item.meta.activeRoutes) {
            if (item.meta.routes?.list) {
                activeRoutes.push(item.meta.routes.list);
            }
            if (item.meta.routes?.detail) {
                activeRoutes.push(item.meta.routes.detail);
            }
      
            // Add children routes if any
            if (item.children) {
                item.children.forEach((child: MenuItem) => {
                    if (child.meta.routes?.list) {activeRoutes.push(child.meta.routes.list);}
                    if (child.meta.routes?.detail) {activeRoutes.push(child.meta.routes.detail);}
                });
            }
        }
    
        // Remove duplicates and sort by length (longer routes first for better matching)
        const uniqueActiveRoutes = [...new Set(activeRoutes)].sort((a, b) => b.length - a.length);
    
        return (
            <SidebarMenuLink 
                route={item.meta.routes?.list || `/${item.path}`}
                activeRoutes={uniqueActiveRoutes}
            >
                {item.meta.icon && <Icon name={normalizeIcon(item.meta.icon) as any} style={{marginRight: '8px'}} />}
                <span className="hide-menu">{item.meta.title}</span>
            </SidebarMenuLink>
        );
    };

    const isSectionDirectLink = (section: MenuSection): boolean => {
        return !!(
            section.meta.routes?.base || 
            section.meta.routes?.list || 
            section.meta.hasTabNavigation ||
            section.meta.breadcrumbs
        );
    };

    const renderSection = (section: MenuSection) => {
        const visibleItems = section.items.filter(item => item.meta.visible !== false);
        const sectionDynamicChildren = dynamicChildrenCache[section.name] || [];
    
        if (isSectionDirectLink(section)) {
            const activeRoutes: string[] = [];
            
            if (section.meta.activeRoutes && section.meta.activeRoutes.length > 0) {
                activeRoutes.push(...section.meta.activeRoutes);
            }
            
            if (section.meta.hasTabNavigation && section.meta.tabs) {
                if (!section.meta.activeRoutes) {
                    section.meta.tabs.forEach(tab => {
                        activeRoutes.push(tab.route);
                    });
                    const baseRoute = section.meta.routes?.base || section.meta.routes?.list;
                    if (baseRoute) {
                        activeRoutes.push(baseRoute);
                    }
                }
            } else {
                if (!section.meta.activeRoutes) {
                    if (section.meta.routes?.list) {
                        activeRoutes.push(section.meta.routes.list);
                    }
                    if (section.meta.routes?.base) {
                        activeRoutes.push(section.meta.routes.base);
                    }
                    if (section.meta.routes?.detail) {
                        activeRoutes.push(section.meta.routes.detail);
                    }
                }
            }
            
            const uniqueActiveRoutes = [...new Set(activeRoutes)].sort((a, b) => b.length - a.length);
            const menuRoute = section.meta.routes?.list || section.meta.routes?.base || `/${section.name}`;
            
            return (
                <SidebarMenuLink 
                    key={section.name}
                    route={menuRoute}
                    activeRoutes={uniqueActiveRoutes}
                >
                    {section.meta.icon && <Icon name={normalizeIcon(section.meta.icon) as any} style={{marginRight: '8px'}} />}
                    <span className="hide-menu">{section.meta.title}</span>
                </SidebarMenuLink>
            );
        }
    
        const sectionMapping: Record<string, string> = {
            'admin': SidebarMenuSections.Admin,
            'discover': SidebarMenuSections.Discover,
            'conversations': SidebarMenuSections.Conversations,
            'insights': SidebarMenuSections.Insights,
            'customers': SidebarMenuSections.Customers,
            'agents': SidebarMenuSections.Agents,
            'teams': SidebarMenuSections.Teams,
            'users': SidebarMenuSections.Admin,
            'reports': SidebarMenuSections.Reports,
            'stories': SidebarMenuSections.Stories,
            'tasks': SidebarMenuSections.Tasks,
            'settings': SidebarMenuSections.Settings,
        };
    
        const sectionEnum = sectionMapping[section.name] || section.meta.title as any;
    
        if (visibleItems.length === 0 && sectionDynamicChildren.length === 0) {
            return null;
        }
    
        return (
            <SidebarMenuSection
                key={section.name}
                name={sectionEnum}
                icon={normalizeIcon(section.meta.icon || 'mdi-folder')}
            >
                {sectionDynamicChildren.map(child => (
                    <SidebarMenuLink 
                        key={child.id}
                        route={child.route}
                        activeRoutes={[child.route]}
                    >
                        {child.icon && <Icon name={normalizeIcon(child.icon) as any} style={{marginRight: '8px'}} />}
                        <span className="hide-menu">{child.title}</span>
                    </SidebarMenuLink>
                ))}
        
                {visibleItems
                    .sort((a, b) => (a.meta.order || 50) - (b.meta.order || 50))
                    .map((item, index) => (
                        <React.Fragment key={`${section.name}-${item.path}-${index}`}>
                            {renderMenuItem(item, section.name)}
                        </React.Fragment>
                    ))
                }
            </SidebarMenuSection>
        );
    };

    if (isLoading) {
        return (
            <div style={{padding: '1rem', textAlign: 'center', color: '#666'}}>
                {i18n.t('jt_loading_menu.text')}
            </div>
        );
    }

    if (error) {
        return (
            <div style={{padding: '1rem', color: '#dc3545'}}>
                <small>{i18n.t('err_cannot_load_menu.text')}</small>
            </div>
        );
    }

    if (menuStructure.length === 0) {
        return (
            <div style={{padding: '1rem', textAlign: 'center', color: '#666'}}>
                <small>{i18n.t('err_no_menu_items_available.text')}</small>
            </div>
        );
    }

    return (
        <>
            {menuStructure
                .sort((a, b) => (a.meta.order || 50) - (b.meta.order || 50))
                .map(renderSection)
                .filter(Boolean) // Remove null sections
            }
        </>
    );
};

export default AutoSidebarMenu;
